import type { CSSProperties } from 'react';
import { RIDGE_HEIGHT, RIDGE_TILE, ridgePath } from '../components/ridges';
import { cn } from '../lib/cn';

/** Back to front: distant pale ridges, two blue ridges, the green valley in front. */
const LAYERS = [
  {
    ys: [118, 64, 96, 132, 80, 38, 72, 110, 90, 54, 100, 128],
    className: 'text-[color-mix(in_srgb,var(--color-brand-500)_50%,white)] animate-ridge-slow',
  },
  {
    ys: [170, 134, 112, 150, 176, 128, 96, 140, 160, 120, 146, 184],
    className: 'text-brand-500 animate-ridge-slow',
    style: { animationDuration: '130s', animationDelay: '-50s' },
  },
  {
    ys: [214, 190, 168, 200, 226, 184, 160, 198, 220, 176, 206, 230],
    className: 'text-[#2B6496] animate-ridge',
  },
  {
    ys: [268, 246, 232, 254, 276, 250, 236, 260, 280, 244, 258, 272],
    className: 'text-valley animate-ridge-fast',
  },
] satisfies { ys: number[]; className: string; style?: CSSProperties }[];

const CLOUDS = [
  // motion-reduce:translate-x-* is where each cloud rests when motion is reduced, clear of the text.
  {
    top: '9%',
    width: 140,
    opacity: 0.14,
    className: 'animate-cloud-slow motion-reduce:translate-x-[68%]',
    delay: '-40s',
  },
  {
    top: '45%',
    width: 210,
    opacity: 0.12,
    className: 'animate-cloud-slow motion-reduce:translate-x-[62%]',
    delay: '-100s',
  },
  {
    top: '52%',
    width: 120,
    opacity: 0.1,
    className: 'animate-cloud motion-reduce:translate-x-[6%]',
    delay: '-25s',
  },
];

function Cloud({ width, opacity }: { width: number; opacity: number }) {
  return (
    <svg viewBox="0 0 120 48" width={width} style={{ opacity }} focusable="false">
      <g fill="white">
        <ellipse cx="60" cy="34" rx="56" ry="14" />
        <circle cx="40" cy="26" r="16" />
        <circle cx="64" cy="20" r="20" />
        <circle cx="86" cy="28" r="13" />
      </g>
    </svg>
  );
}

/**
 * Animated Blue Ridge scene behind the sign-in panel (D36): dawn sky, a sun that rises once and
 * then glows, slow clouds and birds, and four ridge layers drifting in parallax with a band of
 * valley mist. CSS transforms only; every moving layer stops under reduced motion.
 */
export function AuthHero() {
  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden
      data-testid="auth-hero"
    >
      {/* Sunrise haze along the horizon */}
      <div className="absolute inset-x-0 bottom-0 h-3/5 bg-linear-to-t from-sunrise-400/30 via-brand-500/20 to-transparent" />

      {CLOUDS.map((c) => (
        <div
          key={c.top}
          className={cn('absolute inset-x-0 motion-reduce:animate-none', c.className)}
          style={{ top: c.top, animationDelay: c.delay }}
          data-animated
        >
          <Cloud width={c.width} opacity={c.opacity} />
        </div>
      ))}

      <div
        className="absolute inset-x-0 top-[48%] animate-birds motion-reduce:translate-x-[40%] motion-reduce:animate-none"
        style={{ animationDelay: '-18s' }}
        data-animated
      >
        <svg viewBox="0 0 60 24" width="60" focusable="false" className="opacity-60">
          <g fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round">
            <path d="M4 12 q5 -6 10 0 q5 -6 10 0" />
            <path d="M28 6 q4 -5 8 0 q4 -5 8 0" />
            <path d="M40 18 q3 -4 6 0 q3 -4 6 0" />
          </g>
        </svg>
      </div>

      <div className="absolute inset-x-0 bottom-0 h-[46%] max-h-[460px] min-h-64">
        {/* Sun: rises into place once, then breathes behind the far ridge */}
        <div
          className="absolute bottom-[56%] left-[56%] size-44 animate-sun-rise motion-reduce:animate-none"
          data-animated
        >
          <div
            className="absolute inset-0 animate-glow rounded-full bg-sunrise-400/35 blur-2xl motion-reduce:animate-none"
            data-animated
          />
          <div className="absolute inset-[30%] rounded-full bg-sunrise-400 shadow-[0_0_60px_18px_rgba(224,164,59,0.4)]" />
        </div>

        {LAYERS.map((layer, i) => (
          <div key={layer.className} className="contents">
            {i === 2 && (
              <div
                className="absolute inset-x-[-10%] bottom-[30%] h-16 animate-mist rounded-full bg-white opacity-30 blur-2xl motion-reduce:animate-none"
                data-animated
              />
            )}
            <svg
              viewBox={`0 0 ${RIDGE_TILE * 2} ${RIDGE_HEIGHT}`}
              preserveAspectRatio="none"
              className={cn(
                'absolute bottom-0 left-0 h-full w-[400%] motion-reduce:animate-none',
                layer.className,
              )}
              style={'style' in layer ? layer.style : undefined}
              focusable="false"
              data-animated
            >
              <path d={ridgePath(layer.ys)} fill="currentColor" />
            </svg>
          </div>
        ))}
      </div>
    </div>
  );
}
